<template>
  <v-progress-linear
    v-if="loading"
    color="border"
    :indeterminate="true"
    data-testid="linear-progress-bar"
  />
  <div v-else>
    <h3 class="font-weight-bold mb-4" data-testid="news-feed-widget-title">
      News Feed
    </h3>

    <div class="d-flex align-end flex-wrap gap-2">
      <NewsFeedFilterByType @update-feeds="filterNewsFeedsByType" />
      <NewsFeedFilterByDate
        :max-date="maxDate?.toDate()"
        :min-date="minDate?.toDate()"
        @update-feeds="filterNewsFeedsByDate"
      />
    </div>

    <NewsFeeds
      v-if="filteredNewsFeeds.length > 0"
      :news-feeds="filteredNewsFeeds"
    />
    <SoAlert v-else class="my-5" type="info" data-testid="no-news-feeds">
      <p class="text-body-1">No news feed found</p>
    </SoAlert>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { NewsfeedService } from "@/services/NewsfeedService"
  import NewsFeeds from "@/views/customer/search/components/newsFeed/NewsFeeds.vue"
  import NewsFeedFilterByDate from "@/views/customer/search/components/newsFeed/NewsFeedFilterByDate.vue"
  import NewsFeedFilterByType from "@/views/customer/search/components/newsFeed/NewsFeedFilterByType.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import dayjs from "dayjs"

  export default defineComponent({
    components: {
      SoAlert,
      NewsFeedFilterByType,
      NewsFeedFilterByDate,
      NewsFeeds,
    },
    data() {
      return {
        articlesQuery: NewsfeedService.getPublishedArticles(),
        filterType: "",
        filterDate: "",
      }
    },
    computed: {
      newsFeeds() {
        return this.articlesQuery?.data?.items ?? []
      },
      loading() {
        return this.articlesQuery?.isFetching ?? false
      },
      maxDate() {
        const initialLastPublishedFeed = this.findMaxDate(this.newsFeeds)
        const lastPublishedFeed = this.findMaxDate(this.filteredNewsFeeds)

        return (
          dayjs(lastPublishedFeed?.publishDate).startOf("day") ??
          dayjs(initialLastPublishedFeed?.publishDate).startOf("day")
        )
      },
      minDate() {
        const initialFirstPublishedFeed = this.findMinDate(this.newsFeeds)
        const firstPublishedFeed = this.findMinDate(this.filteredNewsFeeds)

        return (
          dayjs(firstPublishedFeed?.publishDate).startOf("day") ??
          dayjs(initialFirstPublishedFeed?.publishDate).startOf("day")
        )
      },
      filteredNewsFeeds() {
        if (!this.filterType && !this.filterDate) {
          return this.newsFeeds
        }

        const startDate = dayjs(this.filterDate[0]).startOf("day")
        const endDate = dayjs(this.filterDate[1]).startOf("day")

        return this.newsFeeds.filter((feed) => {
          const isTypeMatch =
            !this.filterType ||
            (this.filterType === "other"
              ? feed.type === undefined
              : feed.type === this.filterType)

          const isDateRangeMatch =
            !this.filterDate ||
            (dayjs(feed.publishDate).startOf("day") >= startDate &&
              dayjs(feed.publishDate).startOf("day") <= endDate)

          return isTypeMatch && isDateRangeMatch
        })
      },
    },
    methods: {
      filterNewsFeedsByType(newVal) {
        this.filterType = newVal
        return this.filterType
      },
      filterNewsFeedsByDate(newVal) {
        this.filterDate = newVal
        return this.filterDate
      },
      findMaxDate(feeds) {
        return feeds.reduce(
          (prev, current) =>
            prev?.publishDate > current.publishDate ? prev : current,
          feeds[0],
        )
      },
      findMinDate(feeds) {
        return feeds.reduce(
          (prev, current) =>
            prev?.publishDate < current.publishDate ? prev : current,
          feeds[0],
        )
      },
    },
  })
</script>
